// Offline mock implementation of the AIRiskApi (plan §10) — switchable via
// REACT_APP_AI_RISK_MOCK=true (evaluated in getAIRiskApi, api.ts).
//
// Mirrors the server semantics from api_contracts.md EXACTLY:
// - state persisted per project in localStorage (survives reload),
// - persist-before-respond (every mutation writes storage before returning),
// - reanalyze replaces ONLY `suggested` hazards + merges groups by label,
// - initial generate persists `draft` (status → generated); regenerate
//   persists `pending_proposal` (draft untouched, reload-safe diff),
// - accept mints a fake Risk (`mock-` resource_id), marks the hazard
//   accepted (reviewed_at, accepted_risk_resource_id) and 409s on
//   double-accept / unresolved proposal,
// - DELETE discards the session only — `project.risks` is never touched.
//
// Errors are thrown in the axios error SHAPE (`error.response.status/data`)
// so the hooks' status checks work identically against mock and real API.

import {
  AIAcceptResponse,
  AIApiErrorBody,
  AIApiErrorCode,
  AIHazardShell,
  AIHazardStatus,
  AIRiskSession,
} from "../types";
import { Risk } from "components/Risk/types";
import type { AIRiskApi } from "../api";
import {
  FIXTURE_GENERATION_WARNINGS,
  FIXTURE_GROUPS,
  FIXTURE_SKIPPED_CATEGORIES,
  buildDraftForShell,
  buildRegenerationProposal,
} from "./fixtures";

const STORAGE_KEY_PREFIX = "certain:ai-risk-session:";

// Simulated latencies so the multi-phase loaders are visible in demos
// (plan §10); skipped under jest to keep the unit tests fast.
const ANALYZE_LATENCY_MS = 3_000;
const GENERATE_LATENCY_MS = 2_500;
const DEFAULT_LATENCY_MS = 300;

const simulateLatency = (ms: number): Promise<void> =>
  process.env.NODE_ENV === "test"
    ? Promise.resolve()
    : new Promise((resolve) => setTimeout(resolve, ms));

export interface MockApiError extends Error {
  response: { status: number; data: AIApiErrorBody };
}

const mockHttpError = (
  status: number,
  code: AIApiErrorCode,
  message: string
): MockApiError => {
  const error = new Error(message) as MockApiError;
  error.response = { status, data: { error: { code, message } } };

  return error;
};

const storageKey = (projectId: string) => `${STORAGE_KEY_PREFIX}${projectId}`;

const nowIso = () => new Date().toISOString();

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const loadSession = (projectId: string): AIRiskSession | null => {
  const raw = localStorage.getItem(storageKey(projectId));

  if (!raw) return null;

  try {
    return JSON.parse(raw) as AIRiskSession;
  } catch {
    return null;
  }
};

// Persist BEFORE responding — mirrors the binding server rule.
const saveSession = (projectId: string, session: AIRiskSession) => {
  session.updated_at = nowIso();
  localStorage.setItem(storageKey(projectId), JSON.stringify(session));
};

const requireSession = (projectId: string): AIRiskSession => {
  const session = loadSession(projectId);

  if (!session) {
    throw mockHttpError(404, "no_session", "No AI risk session exists.");
  }

  return session;
};

const requireHazard = (
  session: AIRiskSession,
  hazardId: string
): AIHazardShell => {
  const hazard = session.hazards.find((candidate) => candidate.id === hazardId);

  if (!hazard) {
    throw mockHttpError(404, "no_session", `Unknown hazard "${hazardId}".`);
  }

  return hazard;
};

// Backend-minted, opaque, collision-free ids: continue counting past the
// highest existing `mock-haz-<n>` (kept hazards survive a reanalyze).
const nextHazardNumber = (hazards: AIHazardShell[]): number =>
  hazards.reduce((max, hazard) => {
    const match = hazard.id.match(/^mock-haz-(\d+)$/);

    return match ? Math.max(max, Number(match[1])) : max;
  }, 0) + 1;

const buildFreshShells = (startNumber: number, timestamp: string) => {
  let counter = startNumber;

  return FIXTURE_GROUPS.map((group) => ({
    label: group.label,
    description: group.description,
    shells: group.shells.map(
      (shell): AIHazardShell => ({
        id: `mock-haz-${counter++}`,
        name: shell.name,
        description: shell.description,
        reasoning: shell.reasoning,
        severity: shell.severity,
        group_id_hint: shell.group_id_hint,
        status: AIHazardStatus.suggested,
        user_context: "",
        draft: null,
        pending_proposal: null,
        generation_warnings: [],
        skipped_categories: [],
        created_at: timestamp,
        updated_at: timestamp,
      })
    ),
  }));
};

export const getMockAIRiskApi = (): AIRiskApi => ({
  getSession: async (projectId) => {
    await simulateLatency(DEFAULT_LATENCY_MS);

    const session = loadSession(projectId);

    return session ? clone(session) : null;
  },

  analyze: async (projectId, request) => {
    await simulateLatency(ANALYZE_LATENCY_MS);

    const timestamp = nowIso();
    const existing = loadSession(projectId);

    if (request.mode === "reanalyze" && existing) {
      // Replace ONLY `suggested` hazards; generated/accepted/dismissed are
      // untouched. Safe to re-POST after an unknown outcome.
      const keptHazards = existing.hazards.filter(
        (hazard) => hazard.status !== AIHazardStatus.suggested
      );
      const keptIds = keptHazards.map((hazard) => hazard.id);
      // Count over ALL existing hazards (incl. the replaced suggested ones)
      // so replaced ids are never recycled for the fresh shells.
      const freshGroups = buildFreshShells(
        nextHazardNumber(existing.hazards),
        timestamp
      );
      const newShells = freshGroups.flatMap((group) => group.shells);

      // Merge groups BY LABEL: same label reuses the existing group (id and
      // surviving members kept); other existing groups survive only while
      // they still hold non-suggested hazards; empty groups are removed.
      let groupCounter =
        existing.groups.reduce((max, group) => {
          const match = group.id.match(/^mock-grp-(\d+)$/);

          return match ? Math.max(max, Number(match[1])) : max;
        }, 0) + 1;

      const mergedGroups = freshGroups.map((freshGroup) => {
        const existingGroup = existing.groups.find(
          (group) => group.label === freshGroup.label
        );
        const survivingIds = (existingGroup?.hazard_ids || []).filter((id) =>
          keptIds.includes(id)
        );

        return {
          id: existingGroup ? existingGroup.id : `mock-grp-${groupCounter++}`,
          label: freshGroup.label,
          description: freshGroup.description,
          hazard_ids: [
            ...survivingIds,
            ...freshGroup.shells.map((shell) => shell.id),
          ],
        };
      });
      const untouchedGroups = existing.groups
        .filter(
          (group) =>
            !mergedGroups.some((merged) => merged.id === group.id)
        )
        .map((group) => ({
          ...group,
          hazard_ids: group.hazard_ids.filter((id) => keptIds.includes(id)),
        }))
        .filter((group) => group.hazard_ids.length > 0);

      const session: AIRiskSession = {
        ...existing,
        additional_context: request.additional_context,
        language: request.language,
        groups: [...mergedGroups, ...untouchedGroups],
        hazards: [...keptHazards, ...newShells],
        last_analyzed_at: timestamp,
      };

      saveSession(projectId, session);

      return clone(session);
    }

    const freshGroups = buildFreshShells(1, timestamp);
    const session: AIRiskSession = {
      id: `mock-session-${projectId}`,
      project_crn: `crn:project:${projectId}`,
      additional_context: request.additional_context,
      language: request.language,
      groups: freshGroups.map((group, index) => ({
        id: `mock-grp-${index + 1}`,
        label: group.label,
        description: group.description,
        hazard_ids: group.shells.map((shell) => shell.id),
      })),
      hazards: freshGroups.flatMap((group) => group.shells),
      last_analyzed_at: timestamp,
      created_at: timestamp,
      updated_at: timestamp,
    };

    saveSession(projectId, session);

    return clone(session);
  },

  updateHazard: async (projectId, hazardId, patch) => {
    await simulateLatency(DEFAULT_LATENCY_MS);

    const session = requireSession(projectId);
    const hazard = requireHazard(session, hazardId);

    if (hazard.status === AIHazardStatus.accepted) {
      throw mockHttpError(
        409,
        "invalid_state",
        "Accepted hazards are immutable."
      );
    }
    if (patch.status === AIHazardStatus.accepted) {
      throw mockHttpError(
        409,
        "invalid_state",
        "Hazards can only be accepted through the accept endpoint."
      );
    }

    if (patch.name !== undefined) hazard.name = patch.name;
    if (patch.description !== undefined) hazard.description = patch.description;
    if (patch.user_context !== undefined)
      hazard.user_context = patch.user_context;
    if (patch.status !== undefined) hazard.status = patch.status;
    if (patch.draft !== undefined) hazard.draft = patch.draft;
    if (patch.pending_proposal !== undefined)
      hazard.pending_proposal = patch.pending_proposal;

    hazard.updated_at = nowIso();
    saveSession(projectId, session);

    return clone(hazard);
  },

  generateHazardDetail: async (projectId, hazardId, request) => {
    await simulateLatency(GENERATE_LATENCY_MS);

    const session = requireSession(projectId);
    const hazard = requireHazard(session, hazardId);

    if (hazard.status === AIHazardStatus.accepted) {
      throw mockHttpError(
        409,
        "invalid_state",
        "Accepted hazards are immutable."
      );
    }

    // The freshest shell values from the request are persisted onto the
    // shell (unsaved edits are honored).
    hazard.name = request.name;
    hazard.description = request.description;
    hazard.user_context = request.user_context;

    if (request.mode === "regenerate") {
      if (!hazard.draft) {
        throw mockHttpError(
          409,
          "invalid_state",
          "Cannot regenerate before an initial draft exists."
        );
      }

      // Draft untouched — the persisted proposal makes the diff reload-safe.
      hazard.pending_proposal = buildRegenerationProposal(hazard.draft);
    } else {
      hazard.draft = buildDraftForShell(request.name, request.description);
      hazard.status = AIHazardStatus.generated;
    }

    // Latest generation's warnings + skipped categories, persisted.
    hazard.generation_warnings = clone(FIXTURE_GENERATION_WARNINGS);
    hazard.skipped_categories = clone(FIXTURE_SKIPPED_CATEGORIES);

    hazard.updated_at = nowIso();
    saveSession(projectId, session);

    return clone(hazard);
  },

  acceptHazard: async (projectId, hazardId, request) => {
    await simulateLatency(DEFAULT_LATENCY_MS);

    const session = requireSession(projectId);
    const hazard = requireHazard(session, hazardId);

    if (hazard.status === AIHazardStatus.accepted) {
      // Idempotency path: the client re-GETs the session on 409.
      throw mockHttpError(409, "invalid_state", "Hazard already accepted.");
    }
    if (hazard.pending_proposal) {
      throw mockHttpError(
        409,
        "invalid_state",
        "Resolve the pending regeneration proposal before accepting."
      );
    }
    if (request.acknowledged !== true) {
      throw mockHttpError(
        422,
        "vocabulary_violation",
        "Accept requires explicit acknowledgment."
      );
    }

    const timestamp = nowIso();
    const acceptedCount = session.hazards.filter(
      (candidate) => candidate.accepted_risk_resource_id
    ).length;
    const resourceId = `mock-risk-${acceptedCount + 1}`;

    // Minted like POST /projects/:id/risks would — the user-decided
    // fully_mitigated value from the Accept Gate is applied here.
    const risk: Risk = {
      ...request.risk,
      fully_mitigated: request.fully_mitigated,
      id: `mock-risk-id-${acceptedCount + 1}`,
      resource_id: resourceId,
      created_at: timestamp,
    };

    hazard.status = AIHazardStatus.accepted;
    hazard.reviewed_at = timestamp;
    hazard.accepted_risk_resource_id = resourceId;
    hazard.pending_proposal = null;
    hazard.updated_at = timestamp;

    saveSession(projectId, session);

    return clone<AIAcceptResponse>({
      risk,
      risk_grouping_id: `mock-rg-${request.group_label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")}`,
      session,
    });
  },

  discardSession: async (projectId) => {
    await simulateLatency(DEFAULT_LATENCY_MS);

    requireSession(projectId);
    localStorage.removeItem(storageKey(projectId));
  },
});
